import type { GenericLocation, UnionKeys } from './location';

type ValueAtKey<
  ParamValues,
  Key extends PropertyKey,
> = ParamValues extends object
  ? Key extends keyof ParamValues
    ? ParamValues[Key]
    : never
  : never;

/**
 * Type-safe helper to get a search param when the location might not have it.
 * Returns the search param value or undefined if not present.
 */
export const getSearchParam = <
  Location extends GenericLocation,
  Key extends UnionKeys<NonNullable<Location['searchParams']>>,
>(
  location: Location,
  key: Key
): ValueAtKey<NonNullable<Location['searchParams']>, Key> | undefined => {
  const searchParams = location.searchParams;
  if (!searchParams) {
    return undefined;
  }

  return (
    searchParams as Partial<
      Record<Key, ValueAtKey<NonNullable<Location['searchParams']>, Key>>
    >
  )[key];
};

/**
 * Type-safe helper to get a hidden param when the location might not have it.
 * Returns the hidden param value or undefined if not present.
 */
export const getHiddenParam = <
  Location extends GenericLocation,
  Key extends UnionKeys<NonNullable<Location['hiddenParams']>>,
>(
  location: Location,
  key: Key
): ValueAtKey<NonNullable<Location['hiddenParams']>, Key> | undefined => {
  const hiddenParams = location.hiddenParams;
  if (!hiddenParams) {
    return undefined;
  }

  return (
    hiddenParams as Partial<
      Record<Key, ValueAtKey<NonNullable<Location['hiddenParams']>, Key>>
    >
  )[key];
};
